import { z } from 'zod';
import {
  TOW_TYPES,
  type CreateCompletedCallDTO,
  type CreateNewCallDto,
  type CreateScheduledCallDTO,
} from '@relay-towing/api';

/**
 * The kinds of call the dashboard creates, each on its own route: one to
 * run now, one already run, and one to run later.
 */
export const CALL_TYPES = ['new', 'completed', 'scheduled'] as const;
export type CallType = (typeof CALL_TYPES)[number];

/**
 * An account and a pickup location are what a call cannot go without; the
 * rest of the record can be filled in as the job runs. The screen used to
 * say so twice — an asterisk on each label, and a `canSubmit` that greyed
 * the button out without saying why.
 *
 * Shared by the old Create Call screen and the mobile shell's create sheet,
 * so the two ask the same of a call. The old screen asks for none of the
 * kind's fields, so each of them has a default it keeps: a new call, at
 * tow type ANY, with no reason.
 */
export const createCallSchema = z
  .object({
    account: z.string().trim().min(1, 'Account is required'),
    priority: z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']).default('NORMAL'),
    callType: z.enum(CALL_TYPES).default('new'),
    /* Saved as ANY when nobody picks, which is a real rate tier rather
       than a blank — so the field carries no required marker. */
    towType: z.enum(TOW_TYPES).default('ANY'),
    reason: z.string().default(''),
    dispatchNumber: z.string(),
    contactName: z.string(),
    contactPhone: z.string(),
    pickupLocation: z.string().trim().min(1, 'Pickup location is required'),
    destination: z.string(),
    vehicleMake: z.string(),
    vehicleModel: z.string(),
    vehicleYear: z.string(),
    vehicleColor: z.string(),
    // Defaulted, so the old Create Call screen, which shares this schema
    // and never asked for them, keeps working.
    licensePlate: z.string().default(''),
    vin: z.string().default(''),
    drivers: z.array(z.string()).default([]),
    notes: z.string(),
    // A completed call's
    completionTime: z.string().default(''),
    mileage: z.number().optional(),
    totalCharges: z.number().optional(),
    // A scheduled call's, as the date and time inputs give them
    scheduledDate: z.string().default(''),
    scheduledTime: z.string().default(''),
  })
  .superRefine((values, ctx) => {
    // The server runs a scheduled call with no date or time now, which is
    // not what scheduling it meant.
    if (values.callType !== 'scheduled') return;
    if (!values.scheduledDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduledDate'],
        message: 'Pick the date to run it on.',
      });
    }
    if (!values.scheduledTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduledTime'],
        message: 'Pick the time to run it at.',
      });
    }
  });

export type CreateCallValues = z.infer<typeof createCallSchema>;

/** What `useCreateCall` posts, and by its kind, where. */
export type CreateCallRequest =
  | ({ callType: 'new' } & CreateNewCallDto)
  | ({ callType: 'completed' } & CreateCompletedCallDTO)
  | ({ callType: 'scheduled' } & CreateScheduledCallDTO);

/**
 * The form's values as the kind's route takes them: the year and the
 * completion time parsed, and only the kind's own fields sent.
 */
export const toCreateCallRequest = (
  values: CreateCallValues,
  coords: CallCoords
): CreateCallRequest => {
  const {
    callType,
    vehicleYear,
    reason,
    licensePlate,
    vin,
    completionTime,
    mileage,
    totalCharges,
    scheduledDate,
    scheduledTime,
    ...rest
  } = values;
  const call: CreateNewCallDto = {
    ...rest,
    ...coords,
    vehicleYear: vehicleYear ? parseInt(vehicleYear, 10) : undefined,
    reason: reason.trim() || undefined,
    // Plates and VINs are written in capitals; a blank one is not sent.
    licensePlate: licensePlate.trim().toUpperCase() || undefined,
    vin: vin.trim().toUpperCase() || undefined,
  };
  switch (callType) {
    case 'completed':
      return {
        callType,
        ...call,
        // The input's time is the phone's own; the server would read it
        // in its time zone, so it goes as an instant.
        completionTime: completionTime
          ? new Date(completionTime).toISOString()
          : undefined,
        mileage,
        totalCharges,
      };
    case 'scheduled':
      return { callType, ...call, scheduledDate, scheduledTime };
    default:
      return { callType: 'new', ...call };
  }
};

/** A dispatch number from the time and a random tail, as the app has made them. */
export const generateDispatchNumber = (): string => {
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `${timestamp}${random}`;
};

/** Filled in by Places when a suggestion is picked, cleared when typed over. */
export interface CallCoords {
  pickupLocationLat?: number;
  pickupLocationLong?: number;
  destinationLat?: number;
  destinationLong?: number;
}
